import React, { useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import { useState, useEffect } from "react";
import { useAppContext } from "../context/appContext";
import TokenService from "../utils/TokenService";
import MainMenu from "../components/MainMenu";
import { Mixpanel } from "../utils/Mixpanel";
import validator from "validator";
import LoginApi from "../service/LoginApi";
import { jwtDecode } from "jwt-decode";
import { Avatar } from "primereact/avatar";
import { throttle } from "lodash";

const initialState = {
    username: "",
    password: "",
};
const loadScript = (src) =>
    new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve()
        const script = document.createElement('script')
        script.src = src
        script.onload = () => resolve()
        script.onerror = (err) => reject(err)
        document.body.appendChild(script)
    })

const Login = () => {
    const { loginEnabled, displayAlert, setUserName } = useAppContext();
    const navigate = useNavigate();
    const [values, setValues] = useState(initialState);
    const [changesMade, setChangesMade] = useState(false);
    const mobileTopbarActive = false;
    const [isProcessing, setIsProcessing] = useState(false);
    const googleButton = useRef(null);
    const facebookEnabled = true;
    const [isFacebookProcessing, setIsFacebookProcessing] = useState(false);

    const handleChange = (e) => {
        setValues({ ...values, [e.target.name]: e.target.value });
        setChangesMade(true);
    };

    const loginApi = useMutation((values) => {
        const { username, password } = values;
        let wcsrft = TokenService.getCsrfToken('wcsrft');
        try {
            const data = LoginApi.post("auth/login", '{ "email": "' + username + '", "password": "' + password + '", "wcsrft": "' + wcsrft + '"}', {
                withCredentials: true,
            }).then((response) => response.data);
            return data;
        } catch (error) {
            throw error;
        }
    });

    const loginWithFacebookApi = useMutation((body) => {
        try {
            const data = LoginApi.post("auth/login-facebook", body).then((response) => response.data);
            return data;
        } catch (error) {
            throw error;
        }
    });

    const onSubmit = (e) => {
        e.preventDefault();
        submitAction();
    }

    const submitAction = throttle(() => {
        const { username, password } = values;
        if (!username || !password) {
            displayAlert("error", "Error", "Please enter a valid email address and password");
            Mixpanel.track("UI Login - failed all UI validation");
            return;
        }
        if (!validator.isEmail(username)) {
            displayAlert("error", "Error", "Please enter a valid email address");
            Mixpanel.track("UI Login - failed email validation");
            return;
        }
        setIsProcessing(true);
        loginApi.mutate(values, {
            onError: (error) => {
                setIsProcessing(false);
                if (error.response && (error.response.status === 404 || error.response.status === 401)) {
                    displayAlert("error", "Not Found", "Invalid username and/or password");
                    Mixpanel.track("UI Login - failed invalid credentials");
                } else {
                    displayAlert("error", "Unexpected Error", "Please try again later");
                    Mixpanel.track("UI Login - failed API error");
                    console.log(error);
                }
                fetchAuthToken();
            },
            onSuccess: (data) => {
                TokenService.setAccessToken(data.userAccessToken);
                TokenService.setRefreshToken(data.userRefreshToken);
                // displayAlert("success", "Success", "Login successful");
                Mixpanel.register({
                    email: username
                });
                Mixpanel.identify(username);
                Mixpanel.people.set({ "$email": username });
                Mixpanel.track("UI Login - successful");
                setUserName(username);
                if (TokenService.isPendingEmailVerification()) {
                    navigate("/verify-email");
                }
                else if (!TokenService.isUserProfileComplete()) {
                    navigate("/complete-profile");
                }
                else {
                    TokenService.redirectUser(data.providerCount);
                }
            },
        });
    }, 500, { trailing: false });

    const layoutContainerClassName = classNames(
        "layout-wrapper ",
        "pages-body",
        "login-page",
        "flex",
        "flex-column",
        "layout-menu-cyan layout-topbar-cyan",
        {
            "layout-topbar-mobile-active": mobileTopbarActive,
            "p-input-outlined": "outlined",
        }
    );

    const fetchAuthToken = () => {
        return LoginApi.get("auth/tkn")
            .then((response) => response.data)
            .catch((error) => {
                console.error('Failed to fetch token:', error);
                var mixpanelMessage = "Error getting login token: " + error;
                Mixpanel.track("UI ID - Javascript error", {
                    'Javascript Error': mixpanelMessage
                });
            });
    };

    useEffect(() => {
        fetchAuthToken();

        if (TokenService.isAuthenticated()) {
            if (TokenService.isLoggedIn()) {
                if (!TokenService.isUserProfileComplete()) {
                    navigate("/complete-profile");
                } else {
                    navigate("/account/select-provider");
                }
            }
            else {
                navigate("/verify-email");
            }
        }
        else {
            const searchParams = new URLSearchParams(window.location.search);
            if (searchParams.has("promote-my-company")) {
                TokenService.setRedirectOnLogin(process.env.REACT_APP_NETWORK_SITE + "/company/create-profile");
            }
            const googleSrc = 'https://accounts.google.com/gsi/client';
            loadScript(googleSrc)
                .then(() => {
                    window.google.accounts.id.initialize({
                        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                        callback: handleCredentialResponse,
                    })
                    window.google.accounts.id.renderButton(
                        googleButton.current, //this is a ref hook to the div in the official example
                        {
                            type: 'standard',
                            shape: 'rectangular',
                            text: 'signin_with',
                            theme: 'outline',
                            size: 'large',
                            logo_alignment: 'left',
                            width: '300px'
                        })

                })
                .catch(console.error)

            const facebookSrc = 'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v21.0&appId=' + process.env.REACT_APP_FACEBOOK_APP_ID + '&autoLogAppEvents=1';
            loadScript(facebookSrc);

            // if (facebookEnabled) {
            //     const facebookSrc = 'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v21.0&appId=' + process.env.REACT_APP_FACEBOOK_APP_ID + '&autoLogAppEvents=1';
            //     loadScript(facebookSrc)
            //         .then(() => {
            //             window.FB.Event.subscribe('auth.authResponseChange', function (response) {
            //                 if (response.status === 'connected') {
            //                     window.FB.api(
            //                         '/' + response.authResponse.userID + '/?fields=id,name,email',
            //                         'GET',
            //                         {},
            //                         function (apiResponse) {
            //                             let email = apiResponse.email;
            //                             let name = apiResponse.name;

            //                             let body = {}
            //                             body.email = email;
            //                             body.signedRequest = response.authResponse.signedRequest;
            //                             body.facebookUserId = response.authResponse.userID;
            //                             body.name = name;

            //                             // Log into Wallist
            //                             loginWithFacebookApi.mutate(body, {
            //                                 onError: (error) => {
            //                                     displayAlert("error", "Unexpected Error", "Please try again later");
            //                                     Mixpanel.track("UI Facebook Login - failed API error");
            //                                 },
            //                                 onSuccess: (data) => {
            //                                     TokenService.setAccessToken(data.userAccessToken);
            //                                     TokenService.setRefreshToken(data.userRefreshToken);
            //                                     Mixpanel.register({
            //                                         email: email
            //                                     });
            //                                     Mixpanel.identify(email);
            //                                     Mixpanel.people.set({ "$email": email });
            //                                     Mixpanel.track("UI Facebook Login - successful");
            //                                     setUserName(email);
            //                                     TokenService.redirectUser(data.providerCount);
            //                                 },
            //                             });
            //                         }
            //                     );
            //                 }
            //             });
            //         })
            //         .catch(console.error)
            // }
            return () => {
                const googleScriptTag = document.querySelector(`script[src="${googleSrc}"]`)
                if (googleScriptTag) document.body.removeChild(googleScriptTag)
            }
        }
    }, []);

    function handleCredentialResponse(response) {
        const responsePayload = jwtDecode(response.credential);
        let email = responsePayload.email;
        let wcsrft = TokenService.getCsrfToken('wcsrft');
        let body = {
            email: email,
            credential: response.credential,
            wcsrft: wcsrft
        };

        // Log into Wallist
        LoginApi.post("auth/login-google", body).then((response) => {
            setUserName(email);
            TokenService.setAccessToken(response.data.userAccessToken);
            TokenService.setRefreshToken(response.data.userRefreshToken);
            // TODO should be login, not register?
            Mixpanel.register({
                email: email
            });
            Mixpanel.identify(email);
            Mixpanel.people.set({ "$email": email });
            Mixpanel.track("UI Google Login - successful");
            if (!TokenService.isUserProfileComplete()) {
                navigate("/complete-profile");
            } else {
                TokenService.redirectUser(response.data.providerCount);
            }
        }).catch((error) => {
            displayAlert("error", "Unexpected Error", "Unexpected error. Please try again later");
            Mixpanel.track("UI Google Login - failed API error");
            console.log(error);
            fetchAuthToken(); // Re-fetch the token on error
        });
    }

    const handleFacebookLogin = () => {
        try {
            window.FB.login(function (response) {
                if (response.status === 'connected') {
                    setIsFacebookProcessing(true);
                    window.FB.api(
                        '/' + response.authResponse.userID + '/?fields=id,name,email',
                        'GET',
                        {},
                        function (apiResponse) {
                            let email = apiResponse.email;
                            let name = apiResponse.name;
                            let wcsrft = TokenService.getCsrfToken('wcsrft');

                            // Extract first name and last name from full name
                            let firstName = "";
                            let lastName = "";
                            if (name) {
                                const nameParts = name.trim().split(/\s+/);
                                if (nameParts.length > 1) {
                                    firstName = nameParts.slice(0, -1).join(" ");
                                    lastName = nameParts[nameParts.length - 1];
                                } else {
                                    firstName = name;
                                    lastName = "";
                                }
                            }

                            // Store in sessionStorage for CompleteProfile to use
                            if (firstName) {
                                sessionStorage.setItem('facebookFirstName', firstName);
                            }
                            if (lastName) {
                                sessionStorage.setItem('facebookLastName', lastName);
                            }

                            let body = {
                                email,
                                signedRequest: response.authResponse.signedRequest,
                                facebookUserId: response.authResponse.userID,
                                wcsrft: wcsrft
                            };

                            // Log into Wallist
                            loginWithFacebookApi.mutate(body, {
                                onError: (error) => {
                                    displayAlert("error", "Unexpected Error", "Please try again later");
                                    Mixpanel.track("UI Facebook Login - failed API error");
                                    console.log(error);
                                    fetchAuthToken();
                                    setIsFacebookProcessing(false);
                                },
                                onSuccess: (data) => {
                                    TokenService.setAccessToken(data.userAccessToken);
                                    TokenService.setRefreshToken(data.userRefreshToken);
                                    Mixpanel.register({ email });
                                    Mixpanel.identify(email);
                                    Mixpanel.people.set({ "$email": email });
                                    Mixpanel.track("UI Facebook Login - successful");
                                    setUserName(email);
                                    if (!TokenService.isUserProfileComplete()) {
                                        navigate("/complete-profile");
                                    } else {
                                        TokenService.redirectUser(data.providerCount);
                                    }
                                },
                            });
                        }
                    );
                }
            }, { scope: 'public_profile,email' });
        }
        catch (error) {
            displayAlert("error", "Unexpected Error", "Please try again later");
            Mixpanel.track("UI Facebook Login - failed API error");
            console.log(error);
        }
    };

    return (
        <div className={layoutContainerClassName}>
            <MainMenu />
            {loginEnabled && (
                <div className="align-self-center mt-auto mb-auto">
                    <div className="pages-panel card flex flex-column p-4">
                        <h5 className="pages-detail mb-3 font-bold">Sign in using Google{facebookEnabled && " or Facebook"}</h5>
                        <div className="flex mt-1 justify-content-center" style={{ height: "50px" }}>
                            <div ref={googleButton}></div>
                        </div>
                        {facebookEnabled && (
                            <div className="flex mt-1 justify-content-center">
                                <Button
                                    style={{ width: "300px", height: "40px", backgroundColor: "#1877f2", color: "white" }}
                                    onClick={handleFacebookLogin}
                                    loading={isFacebookProcessing}
                                    icon="pi pi-facebook"
                                    label="Continue with Facebook"
                                />
                            </div>
                        )}
                        <div className="grid mt-1 mb-1">
                            <div className="col-5">
                                <hr />
                            </div>
                            <div className="col-2 font-bold">
                                <Avatar label="or" shape="circle" />
                            </div>
                            <div className="col-5">
                                <hr />
                            </div>
                        </div>
                        <h5 className="pages-detail mb-3 font-bold">Sign in with email and password</h5>
                        <form className="form" onSubmit={onSubmit}>
                            <div className="input-panel flex flex-column px-3 mt-2">
                                <div className="p-inputgroup">
                                    <span className="p-float-label">
                                        <InputText
                                            type="text"
                                            id="username"
                                            name="username"
                                            value={values.username}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor="username">Email Address</label>
                                    </span>
                                </div>

                                <div className="p-inputgroup mt-5 mb-5">
                                    <span className="p-float-label">
                                        <InputText
                                            type="password"
                                            id="password"
                                            name="password"
                                            value={values.password}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor="password">Password</label>
                                    </span>
                                </div>
                            </div>
                            <Button
                                className="mb-5 px-7"
                                icon="pi pi-check"
                                label="Login"
                                type="submit"
                                disabled={!changesMade || isProcessing}
                                loading={isProcessing}
                                style={{ width: "200px" }}
                            ></Button>
                        </form>
                        <div>
                            <span>
                                <a href={process.env.REACT_APP_ID_SITE + "/register"}>Create Account</a>
                            </span>
                            <span className="px-3">|</span>
                            <span>
                                <Link to="/forgot-password">Forgot Password</Link>
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {!loginEnabled && (
                <div className="align-self-center mt-auto mb-auto">
                    <div className="pages-panel card flex flex-column">
                        <div className="pages-header mb-6 px-3 py-1">
                            <h2>Login Not Available</h2>
                        </div>

                        <div className="pages-detail mb-6 px-6">
                            We're sorry but this is not available at this time. Please check again later.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Login;
